import type {
	ClientCollection,
	ClientRelease,
} from '../../shared/types/collection'

const COLLECTION_CACHE_KEY = 'collection-pages'
const COLLECTION_CACHE_OWNER_KEY = 'collection-pages-owner'
const COLLECTION_CACHE_VERSION = 3
const MAX_COLLECTION_PAGE_CACHE_ENTRIES = 20

type CachedCollectionPage = {
	cachedAt: number
	collection: ClientCollection
}

type CollectionPagesCache = {
	pages: Record<string, CachedCollectionPage>
	version: number
}

function createEmptyCollectionCache(): CollectionPagesCache {
	return {
		pages: {},
		version: COLLECTION_CACHE_VERSION,
	}
}

function readCollectionCache(): CollectionPagesCache {
	const raw = localStorage.getItem(COLLECTION_CACHE_KEY)
	if (!raw || raw === 'null') return createEmptyCollectionCache()

	try {
		const parsed = JSON.parse(raw) as unknown
		if (isCollectionPagesCache(parsed)) {
			return {
				pages: parsed.pages,
				version: COLLECTION_CACHE_VERSION,
			}
		}
	} catch {
		return createEmptyCollectionCache()
	}

	return createEmptyCollectionCache()
}

function isCollectionPagesCache(value: unknown): value is CollectionPagesCache {
	return (
		!!value &&
		typeof value === 'object' &&
		'version' in value &&
		(value as CollectionPagesCache).version === COLLECTION_CACHE_VERSION &&
		'pages' in value &&
		!!(value as CollectionPagesCache).pages
	)
}

function writeCollectionCache(cache: CollectionPagesCache): void {
	try {
		localStorage.setItem(
			COLLECTION_CACHE_KEY,
			JSON.stringify(pruneCollectionCache(cache)),
		)
	} catch (error) {
		console.warn('Failed to write collection cache', error)
		clearCollectionCache()
	}
}

/**
 * Removes cached collection pages from local storage.
 */
export function clearCollectionCache(): void {
	localStorage.removeItem(COLLECTION_CACHE_KEY)
}

/**
 * Persisted pages survive native relaunches, so they belong to one account.
 * Drops them when a different account is signed in.
 */
export function claimCollectionCache(userId: string): void {
	if (localStorage.getItem(COLLECTION_CACHE_OWNER_KEY) === userId) return
	clearCollectionCache()
	localStorage.setItem(COLLECTION_CACHE_OWNER_KEY, userId)
}

/**
 * Stores a collection result page for quick route switches.
 */
export function setCachedCollection(
	collection: ClientCollection,
	cacheKey: string,
): void {
	const cache = readCollectionCache()
	cache.pages[cacheKey] = {
		cachedAt: Date.now(),
		collection,
	}
	writeCollectionCache(cache)
}

/**
 * Returns a cached collection result page for the exact snapshot/filter key.
 */
export function getCachedCollection(cacheKey: string): ClientCollection | null {
	return readCollectionCache().pages[cacheKey]?.collection ?? null
}

function mergeCachedReleaseAtIndex(
	page: CachedCollectionPage,
	idx: number,
	release: ClientCollection['releases'][number],
): ClientCollection['releases'][number] {
	const mergedRelease = {
		...page.collection.releases[idx],
		...release,
		history: release.history ?? page.collection.releases[idx].history,
	}
	page.collection.releases[idx] = mergedRelease
	page.collection.timestamp = page.collection.timestamp ?? Date.now()
	page.cachedAt = Date.now()
	return mergedRelease
}

function updateCachedRelease(
	release: ClientCollection['releases'][number],
	matches: (cachedRelease: ClientRelease) => boolean,
): ClientCollection['releases'][number] | undefined {
	const cache = readCollectionCache()
	let merged: ClientCollection['releases'][number] | undefined

	Object.values(cache.pages).forEach((page) => {
		const idx = page.collection.releases.findIndex(matches)
		if (idx === -1) return
		merged = mergeCachedReleaseAtIndex(page, idx, release)
	})

	if (merged) writeCollectionCache(cache)
	return merged ?? release
}

/**
 * Merges a release into the cached collection payload by collection instance id.
 */
export function updateCachedReleaseByInstanceId(
	release: ClientCollection['releases'][number],
	instanceId: number | string | null | undefined = release.id,
): ClientCollection['releases'][number] | undefined {
	const targetId = Number(instanceId)
	if (!Number.isFinite(targetId)) return release

	return updateCachedRelease(release, (cachedRelease) => {
		return Number(cachedRelease.id) === targetId
	})
}

/**
 * Merges a release into the cached collection payload by Discogs release id.
 */
export function updateCachedReleaseByReleaseId(
	release: ClientCollection['releases'][number],
	releaseId: number | string | null | undefined = release.data?.id,
): ClientCollection['releases'][number] | undefined {
	const targetId = Number(releaseId)
	if (!Number.isFinite(targetId)) return release

	return updateCachedRelease(release, (cachedRelease) => {
		return Number(cachedRelease.data?.id) === targetId
	})
}

/**
 * Looks up a release in the cached collection by collection instance id.
 */
export function getCachedReleaseByInstanceId(
	instanceId: number | string,
): ClientRelease | undefined {
	const needle = Number(instanceId)
	return findCachedRelease((release) => Number(release.id) === needle)
}

/**
 * Looks up a release in the cached collection by Discogs release id.
 */
export function getCachedReleaseByReleaseId(
	releaseId: number | string,
): ClientRelease | undefined {
	const needle = Number(releaseId)
	return findCachedRelease((release) => Number(release.data?.id) === needle)
}

function findCachedRelease(
	matches: (release: ClientRelease) => boolean,
): ClientRelease | undefined {
	const cache = readCollectionCache()
	for (const page of Object.values(cache.pages)) {
		const release = page.collection.releases.find(matches)
		if (release) return release
	}
	return undefined
}

function pruneCollectionCache(
	cache: CollectionPagesCache,
): CollectionPagesCache {
	const entries = Object.entries(cache.pages)
		.sort(([, a], [, b]) => b.cachedAt - a.cachedAt)
		.slice(0, MAX_COLLECTION_PAGE_CACHE_ENTRIES)

	return {
		pages: Object.fromEntries(entries),
		version: COLLECTION_CACHE_VERSION,
	}
}
